import { Link } from 'gatsby'
import cx from 'classnames'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  BlackCaret,
  BlackCloseIcon,
  BlackMenuIcon,
  LogoDark
} from 'src/components/svgs'
import {
  HeadlineLink,
  ChildLink,
  GrandchildLink,
} from '../interfaces/HeaderNavbar'
import { GlobalBannerProps } from 'src/interfaces/GlobalBanner'
import classes from './header.module.css'
import { AcuityButtonType, trackButtonClick } from 'src/utils/accuityEvents'
import GlobalBanner from './global/GlobalBanner'
import { useVariant } from 'src/AmplitudeExperimentProvider'
import { getSignupUrl } from './global/joinWidget'
import { trackWebsiteCtaClicked } from 'src/utils/ctaTracking'

const onTrackNavigation = async (buttonType: AcuityButtonType, url: string) => {
  const resultObject: { success: boolean; error: unknown } = {
    success: false,
    error: undefined,
  }

  try {
    trackButtonClick(buttonType)
    resultObject.success = true
  } catch (reason) {
    resultObject.error = reason
  }

  if (typeof window !== 'undefined') {
    const timeout = setTimeout(() => {
      window.location.href = url
    }, 300)

    return () => {
      clearTimeout(timeout)
    }
  }
}

// Backward compatibility helpers - checks both field names
// The actual Sanity field is 'sublinks', but we support legacy 'children'/'grandchildren' too
const getChildren = (parent: HeadlineLink): ChildLink[] =>
  parent.sublinks || parent.children || []

const getGrandchildren = (child: ChildLink): GrandchildLink[] =>
  child.sublinks || child.grandchildren || []

// Normalize link to ensure it has a trailing slash
// Handles: '/path' -> '/path/', '/path/' -> '/path/', '#' -> '#', '' -> '#'
const normalizeLink = (link: string | undefined): string => {
  if (!link || link === '#' || link === '') return '#'
  // Don't add trailing slash to external URLs or hash links
  if (link.startsWith('http') || link.startsWith('#')) return link
  // Add trailing slash if not present
  return link.endsWith('/') ? link : `${link}/`
}

export const Header = (props: {
  headerNavProps: HeadlineLink[]
  globalBannerProps: GlobalBannerProps
  location: string
  shouldShowGlobalBanner: boolean
  abbreviatedNavBar: boolean | undefined
  setShouldShowGlobalBanner: (value: boolean) => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [headlineLinks, setHeadlineLinks] = useState<HeadlineLink[]>([])
  const [globalBannerProps, setGlobalBannerProps] = useState<
    GlobalBannerProps
  >()
  const [globalBannerVariant, setGlobalBannerVariant] = useState<
    'control' | 'treatment' | undefined
  >('control')
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([])
  const [openGrandchildMenus, setOpenGrandchildMenus] = useState<string[]>([])
  const [isHeaderHidden, setIsHeaderHidden] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const prevScrollY = useRef(0)
  const scrollThreshold = useRef(0)
  const ticking = useRef(false)

  useEffect(() => {
    // Use a fixed scroll threshold for editorial pages (those starting with '/article')
    const isEditorial = typeof window !== 'undefined' && window.location.pathname.startsWith('/article')
    if (isEditorial) {
      scrollThreshold.current = 400 // Fixed threshold for editorials
    } else {
      const main = document.getElementById('maincontent')
      if (main && main.firstElementChild) {
        const firstBlock = main.firstElementChild as HTMLElement
        scrollThreshold.current =
          firstBlock.offsetTop + firstBlock.offsetHeight
      } else {
        scrollThreshold.current = 0
      }
    }

    if (typeof window !== 'undefined') {
      prevScrollY.current = window.scrollY
    } else {
      prevScrollY.current = 0
    }

    setIsHeaderHidden(false)
  }, [props.location])

  const handleScroll = useCallback(() => {
    if (ticking.current) return
    ticking.current = true
    requestAnimationFrame(() => {
      const currentY = window.scrollY
      const threshold = scrollThreshold.current

      if (currentY < threshold) {
        setIsHeaderHidden(false)
      } else if (currentY > prevScrollY.current) {
        setIsHeaderHidden(true)
      } else {
        setIsHeaderHidden(false)
      }

      prevScrollY.current = currentY
      ticking.current = false
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    setHeadlineLinks(props.headerNavProps)
  }, [props.headerNavProps])

  useEffect(() => {
    setGlobalBannerProps(props.globalBannerProps)
  }, [props.globalBannerProps])

  const closeAllMenus = () => {
    setIsOpen(false)
    setOpenSubmenus([])
    setOpenGrandchildMenus([])
  }

  const openSubmenu = (key: string) => {
    setOpenSubmenus([key])
  }

  const closeSubmenu = () => {
    setOpenSubmenus([])
  }

  const openGrandchildMenuOnHover = (key: string) => {
    setOpenGrandchildMenus([key])
  }

  const closeGrandchildMenu = () => {
    setOpenGrandchildMenus([])
  }

  const toggleGrandchildMenuMobile = (key: string) => {
    const menus = [...openGrandchildMenus]
    const indexOfKey = menus?.findIndex(item => item === key)

    if (indexOfKey === -1) {
      menus.push(key)
      setOpenGrandchildMenus(menus)
    } else {
      menus.splice(indexOfKey, 1)
      setOpenGrandchildMenus(menus)
    }
  }

  const toggleSubmenuMobile = (key: string) => {
    const submenus = [...openSubmenus]
    const indexOfKey = submenus?.findIndex(item => item === key)

    if (indexOfKey === -1) {
      submenus.push(key)
      setOpenSubmenus(submenus)
    } else {
      submenus.splice(indexOfKey, 1)
      setOpenSubmenus(submenus)
    }
  }

  const onJoinTiaClick = (
    event: React.MouseEvent,
    cta?: string,
    module = 'Header nav'
  ) => {
    event.preventDefault()
    const section = 'navbar'
    const targetUrl = getSignupUrl({ cta, section }) || '/'
    const ctaText =
      typeof cta === 'string'
        ? cta.replace(/\s*\(header\)$/, '')
        : 'Join Tia'

    trackWebsiteCtaClicked({
      ctaText,
      module,
      heading: module,
      targetUrl,
    })
    onTrackNavigation(AcuityButtonType.JOIN_TIA, targetUrl)
  }

  const showGlobalBanner = useVariant('global-banner')
  const globalBannerVariants = useVariant('global-banner-variants')
  const joinCtaVariant = useVariant('join-cta', {
    defaultValue: 'control',
    defaultPayload: { cta: 'Join Tia' },
  })

  useEffect(() => {
    props?.setShouldShowGlobalBanner(showGlobalBanner.value === 'treatment')
    setGlobalBannerVariant(globalBannerVariants.value)
  }, [showGlobalBanner, globalBannerVariants])

  if (typeof window === 'undefined') {
    return null
  }

  return (
    <>
      <div
        ref={headerRef}
        className={cx(classes.headerContainer, {
          [classes.headerHidden]: isHeaderHidden && !isOpen,
        })}
      >
        {props?.shouldShowGlobalBanner && (
          <GlobalBanner
            classes={classes}
            globalBannerProps={globalBannerProps}
            globalBannerVariant={globalBannerVariant}
          />
        )}
        <header className="header x bcc f aic top z8 left">
          <div
            className={cx(
              classes.headerInner,
              'container--l px1 py05 outer header__inner mxa x'
            )}
          >
            <div className="f jcb aic x">
              <div className={cx(classes.navWrapper, 'f jcs aic rel')}>
                <Link
                  aria-label="navigate to homepage"
                  onClick={() => closeAllMenus()}
                  className="block header__logo-wrap"
                  to="/"
                >
                  <LogoDark className="header__logo block" />
                </Link>
                <nav
                  className={cx(classes.headline_links, 'nav_show__desktop')}
                >
                  {!props.abbreviatedNavBar && (
                    <ul>
                      {headlineLinks?.map(headlineLink => {
                        const children = getChildren(headlineLink)

                        // Parent without children - render as direct link
                        if (children.length === 0) {
                          return (
                            <li
                              className={cx(classes.navText, 'mono')}
                              key={headlineLink._key}
                            >
                              <Link to={normalizeLink(headlineLink.link)}>
                                {headlineLink.title}
                              </Link>
                            </li>
                          )
                        }

                        // Parent with children - render as dropdown with clickable parent
                        return (
                          <li
                            className={cx(classes.navText, 'mono')}
                            key={headlineLink._key}
                            onMouseEnter={() => openSubmenu(headlineLink._key)}
                            onMouseLeave={() => closeSubmenu()}
                          >
                            <div className={cx('f aic', classes.parentLinkWrapper)}>
                              {headlineLink.link ? (
                                <Link
                                  onClick={() => closeAllMenus()}
                                  to={normalizeLink(headlineLink.link)}
                                >
                                  {headlineLink.title}
                                </Link>
                              ) : (
                                <span>{headlineLink.title}</span>
                              )}
                              <span
                                className="no-style"
                                aria-label={`${headlineLink.title} menu`}
                                style={{ pointerEvents: 'none' }}
                              >
                                <BlackCaret />
                              </span>
                            </div>
                            <ul
                              className={cx(
                                classes.navText,
                                'bccream',
                                classes.sublinks_menu,
                                openSubmenus.includes(headlineLink._key) &&
                                  classes.visible
                              )}
                            >
                              {children.map(child => {
                                const grandchildren = getGrandchildren(child)

                                // child without grandchildren - render as direct link
                                if (grandchildren.length === 0) {
                                  return (
                                    <li key={child._key}>
                                      <Link
                                        onClick={() => closeAllMenus()}
                                        to={normalizeLink(child.link)}
                                      >
                                        {child.title}
                                      </Link>
                                    </li>
                                  )
                                }

                                // child with grandchildren - render as nested dropdown with clickable child
                                return (
                                  <li
                                    key={child._key}
                                    className={classes.has_grandchildren}
                                    onMouseEnter={() =>
                                      openGrandchildMenuOnHover(child._key)
                                    }
                                    onMouseLeave={() => closeGrandchildMenu()}
                                  >
                                    <div
                                      className={cx(
                                        classes.childWithGrandchildren,
                                        'f aic',
                                        classes.childLinkWrapper
                                      )}
                                    >
                                      {child.link ? (
                                        <Link
                                          onClick={() => closeAllMenus()}
                                          to={normalizeLink(child.link)}
                                        >
                                          {child.title}
                                        </Link>
                                      ) : (
                                        <span>{child.title}</span>
                                      )}
                                      <span
                                        className="no-style"
                                        aria-label={`${child.title} menu`}
                                        style={{ pointerEvents: 'none' }}
                                      >
                                        <BlackCaret />
                                      </span>
                                    </div>
                                    <ul
                                      className={cx(
                                        'bccream',
                                        classes.grandchildren_menu,
                                        openGrandchildMenus.includes(
                                          child._key
                                        ) && classes.visible
                                      )}
                                    >
                                      {grandchildren.map(grandchild => (
                                        <li key={grandchild._key}>
                                          <Link
                                            onClick={() => closeAllMenus()}
                                            to={normalizeLink(grandchild.link)}
                                          >
                                            {grandchild.title}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                )
                              })}
                            </ul>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </nav>
              </div>
              <div
                className={cx(
                  classes.fixedLinks,
                  'header__desktop-links f jcb aic'
                )}
                style={
                  props.abbreviatedNavBar ? { justifyContent: 'flex-end' } : {}
                }
              >
                <a
                  onClick={e =>
                    onJoinTiaClick(
                      e,
                      joinCtaVariant?.payload?.cta || 'Join Tia (header)'
                    )
                  }
                  href={getSignupUrl({
                    cta: joinCtaVariant?.payload?.cta || 'Join Tia',
                  })}
                  className={cx(
                    classes.joinButton,
                    'button--join bcorange jcc aic f rel'
                  )}
                >
                  {joinCtaVariant?.payload?.cta || 'Join Tia'}
                </a>
                {!props.abbreviatedNavBar && (
                  <a
                    href={`${
                      process.env.GATSBY_WIDGET_URL?.split('/signup')[0]
                    }`}
                    className="cb caps mono"
                  >
                    Log in
                  </a>
                )}
              </div>

              <div className="header__mobile-toggle">
                <div className="jcs f aic headerJoinWrapper">
                  <a
                    onClick={e =>
                      onJoinTiaClick(
                        e,
                        joinCtaVariant?.payload?.cta || 'Join Tia'
                      )
                    }
                    href={getSignupUrl({
                      cta: joinCtaVariant?.payload?.cta || 'Join Tia (header)',
                    })}
                    className={cx(
                      classes.joinButton,
                      'button--join bcorange jcc aic f rel',
                      'headerJoin'
                    )}
                  >
                    {joinCtaVariant?.payload?.cta || 'Join Tia'}
                  </a>
                </div>
                {!props.abbreviatedNavBar && (
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="caps mono h5 w100 block no-style cb bcc"
                  >
                    {isOpen ? <BlackCloseIcon /> : <BlackMenuIcon />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      </div>
      <div className={cx(classes.mobileMenu, isOpen && classes.isOpen)}>
        <ul>
          {headlineLinks?.map(headlineLink => {
            const children = getChildren(headlineLink)

            // Parent without children - render as direct link
            if (children.length === 0) {
              return (
                <li key={headlineLink._key}>
                  <Link
                    onClick={() => closeAllMenus()}
                    to={normalizeLink(headlineLink.link)}
                  >
                    {headlineLink.title}
                  </Link>
                </li>
              )
            }

            // Parent with children - render as expandable with clickable parent
            return (
              <li
                className={cx({
                  [classes.visible]: openSubmenus.includes(headlineLink._key),
                })}
                key={headlineLink._key}
              >
                <div
                  className="f aic"
                  style={{ justifyContent: 'space-between' }}
                >
                  <span
                    onClick={() => {
                      const isExpanded = openSubmenus.includes(
                        headlineLink._key
                      )
                      // If expanded and has link, navigate; otherwise toggle expand
                      if (isExpanded && headlineLink.link) {
                        closeAllMenus()
                        // Use setTimeout to ensure state updates before navigation
                        setTimeout(() => {
                          window.location.href = normalizeLink(
                            headlineLink.link
                          )
                        }, 0)
                      } else {
                        toggleSubmenuMobile(headlineLink._key)
                      }
                    }}
                    style={{
                      cursor:
                        headlineLink.link &&
                        openSubmenus.includes(headlineLink._key)
                          ? 'pointer'
                          : 'default',
                      flex: 1,
                    }}
                  >
                    {headlineLink.title}
                  </span>
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      toggleSubmenuMobile(headlineLink._key)
                    }}
                    className="no-style"
                    aria-label={`Toggle ${headlineLink.title} menu`}
                  >
                    <BlackCaret />
                  </button>
                </div>
                <ul className={classes.childrenMenu}>
                  {children.map(child => {
                    const grandchildren = getGrandchildren(child)

                    // Child without grandchildren - render as direct link
                    if (grandchildren.length === 0) {
                      return (
                        <li key={child._key}>
                          <Link
                            onClick={() => closeAllMenus()}
                            to={normalizeLink(child.link)}
                          >
                            {child.title}
                          </Link>
                        </li>
                      )
                    }

                    // Child with grandchildren - render as nested expandable with clickable child
                    return (
                      <li
                        key={child._key}
                        className={cx({
                          [classes.visible]: openGrandchildMenus.includes(
                            child._key
                          ),
                        })}
                      >
                        <div
                          className="f aic"
                          style={{ justifyContent: 'space-between' }}
                        >
                          <span
                            onClick={() => {
                              const isExpanded = openGrandchildMenus.includes(
                                child._key
                              )
                              // If expanded and has link, navigate; otherwise toggle expand
                              if (isExpanded && child.link) {
                                closeAllMenus()
                                // Use setTimeout to ensure state updates before navigation
                                setTimeout(() => {
                                  window.location.href = normalizeLink(
                                    child.link
                                  )
                                }, 0)
                              } else {
                                toggleGrandchildMenuMobile(child._key)
                              }
                            }}
                            style={{
                              cursor:
                                child.link &&
                                openGrandchildMenus.includes(child._key)
                                  ? 'pointer'
                                  : 'default',
                              flex: 1,
                            }}
                          >
                            {child.title}
                          </span>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              toggleGrandchildMenuMobile(child._key)
                            }}
                            className="no-style"
                            aria-label={`Toggle ${child.title} menu`}
                          >
                            <BlackCaret />
                          </button>
                        </div>
                        <ul className={classes.grandchildrenMenu}>
                          {grandchildren.map(grandchild => (
                            <li key={grandchild._key}>
                              <Link
                                onClick={() => closeAllMenus()}
                                to={normalizeLink(grandchild.link)}
                              >
                                {grandchild.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    )
                  })}
                </ul>
              </li>
            )
          })}
          <li>
            <a
              onClick={() => closeAllMenus()}
              href={`${process.env.GATSBY_WIDGET_URL?.split('/signup')[0]}`}
            >
              Log in
            </a>
          </li>
        </ul>
        <div className={classes.mobileButtons}>
          <a
            onClick={e =>
              onJoinTiaClick(e, "See if you're in network", 'Mobile menu')
            }
            href={getSignupUrl({
              cta: "See if you're in network",
            })}
            className={classes.mobileButtonPrimary}
          >
            See if you're in network
          </a>
          <Link
            to="/membership/"
            onClick={() => {
              trackWebsiteCtaClicked({
                ctaText: 'Learn about membership',
                module: 'Mobile menu',
                heading: 'Mobile menu',
                targetUrl: '/membership/',
              })
              closeAllMenus()
            }}
            className={classes.mobileButtonSecondary}
          >
            Learn about membership
          </Link>
        </div>
      </div>
    </>
  )
}
